import { useCurrentStateAndParams, useRouter } from '@uirouter/react';

import { useIsEdgeAdmin } from '@/react/hooks/useUser';
import { useUnauthorizedRedirect } from '@/react/hooks/useUnauthorizedRedirect';
import { useEnvironment } from '@/react/portainer/environments/queries/useEnvironment';
import { useGroup } from '@/react/portainer/environments/environment-groups/queries';
import {
  EnvironmentId,
  EnvironmentType,
} from '@/react/portainer/environments/types';
import { isKubernetesEnvironment } from '@/react/portainer/environments/utils';
import { PolicyOverrideAlert } from '@/react/portainer/policies/components/PolicyOverrideAlert';

import { PageHeader } from '@@/PageHeader';

import { useAccesses } from '../queries/useAccesses';
import { PolicyType } from '../../policies/types';
import { usePolicyMetadata } from '../../policies/queries/usePolicyMetadata';

import { EnvironmentDetailsWidget } from './EnvironmentDetailsWidget';
import { CreateAccessWidget } from './CreateAccessWidget';
import { AccessDatatableWrapper } from './AccessDatatableWrapper';

export function AccessView() {
  useUnauthorizedRedirect(
    { authorizations: ['EndpointResourcesAccess'], adminOnlyCE: true },
    { to: 'portainer.endpoints' }
  );
  const {
    params: { id },
  } = useCurrentStateAndParams();
  const router = useRouter();

  const environmentId = parseInt(id, 10) as EnvironmentId;
  const environmentQuery = useEnvironment(environmentId);
  const groupQuery = useGroup(environmentQuery.data?.GroupId);
  useUnauthorizedRedirect(
    { authorizations: ['EndpointResourcesAccess'], adminOnlyCE: true },
    { to: 'portainer.endpoints' }
  );

  const environment = environmentQuery.data;
  const group = groupQuery.data;
  const metadataQuery = usePolicyMetadata();
  const minimumAgentVersions = metadataQuery.data?.minimumAgentVersions;
  const { isAdmin, isLoading: isAdminLoading } = useIsEdgeAdmin({
    forceEnvironmentId: environmentId,
  });
  const {
    hasPolicyOverride,
    policyID,
    policyName,
    isLoading: isAccessesLoading,
  } = useAccesses(environment, group, minimumAgentVersions, isAdmin);
  const policyType: PolicyType = isKubernetesEnvironment(
    environment?.Type ?? EnvironmentType.Docker
  )
    ? 'rbac-k8s'
    : 'rbac-docker';

  const isLoading =
    environmentQuery.isLoading ||
    groupQuery.isLoading ||
    isAdminLoading ||
    isAccessesLoading;

  return (
    <>
      <PageHeader
        title="Environment access"
        breadcrumbs={[
          { label: 'Environments', link: 'portainer.endpoints' },
          {
            label: environment?.Name ?? '',
            link: 'portainer.endpoints.endpoint',
            linkParams: { id: environmentId },
          },
          'Access management',
        ]}
        reload
      />
      {hasPolicyOverride && !isAccessesLoading && (
        <div className="row">
          <div className="col-sm-12">
            <PolicyOverrideAlert
              policyName={policyName ?? ''}
              policyId={policyID ?? 0}
              policyType={policyType}
            />
          </div>
        </div>
      )}
      <EnvironmentDetailsWidget
        environment={environment}
        group={group}
        isLoading={isLoading}
      />
      {isAdmin && !hasPolicyOverride && !isLoading && (
        <CreateAccessWidget
          environment={environment}
          group={groupQuery.data}
          minimumAgentVersions={minimumAgentVersions}
          onSuccess={() => router.stateService.reload()}
          isLoading={isLoading}
        />
      )}
      {isAdmin && (
        <AccessDatatableWrapper
          environment={environment}
          group={groupQuery.data}
          minimumAgentVersions={minimumAgentVersions}
        />
      )}
    </>
  );
}
